import { useState, useMemo, useEffect, useRef } from "react";
import { DefaultViewControl } from "@/components/DefaultViewControl";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Field } from "@/pages/Companies";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { SelectAcceptedQuoteDialog } from "@/components/SelectAcceptedQuoteDialog";
import { Plus, Search, LayoutGrid, List, Pencil, Trash2, Tag, Calendar as CalIcon, FileText, CheckCircle2, Loader2, User, Phone, Mail, MapPin, Paperclip, Send, Star, ShieldCheck, Hourglass, Undo2, Check, CheckCheck, XCircle, Hammer, Eye } from "lucide-react";
import { STAGE_LABELS, STAGE_ORDER, STAGE_COLORS, STAGE_STEP, FENCE_LABELS, fmtCurrency, fmtDate } from "@/lib/constants";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, closestCenter } from "@dnd-kit/core";
import { AuthImg, openTimelineFile } from "@/components/AuthFile";
import { useAuth } from "@/lib/auth";
import ActivityDetailDialog from "@/components/ActivityDetailDialog";
import { fmtDateTime } from "@/lib/constants";

const EMPTY = { title: "", company_id: "none", contact_id: "none", value: 0, stage: "enquiry", fence_type: "chain_wire", expected_close: "", notes: "", tags: [] };

// Expected quote date defaults to 48 hrs from now (Trent, Jun 2026).
const plus48h = () => {
  const d = new Date(Date.now() + 48 * 3600 * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function Deals() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [view, setView] = useState("list");
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [openClosed, setOpenClosed] = useState("open");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [dealTab, setDealTab] = useState("details");
  const [activeId, setActiveId] = useState(null);
  const [tagInput, setTagInput] = useState("");

  const { data: deals = [] } = useQuery({
    queryKey: ["deals", search, stageFilter, openClosed],
    queryFn: async () => {
      const params = {};
      if (search) params.search = search;
      if (stageFilter !== "all") params.stage = stageFilter;
      else if (openClosed !== "all") params.status = openClosed;
      return (await api.get("/deals", { params })).data;
    },
  });
  const { data: companies = [] } = useQuery({ queryKey: ["companies-all"], queryFn: async () => (await api.get("/companies")).data });
  const { data: contacts = [] } = useQuery({ queryKey: ["contacts-all"], queryFn: async () => (await api.get("/contacts")).data });
  const { data: fenceTypes = [] } = useQuery({ queryKey: ["fence-types"], queryFn: async () => (await api.get("/fence-types")).data });
  const { data: unreadData } = useQuery({
    queryKey: ["deals-unread"],
    queryFn: async () => (await api.get("/deals/unread/summary")).data,
    refetchInterval: 60000,
  });
  const unreadMap = unreadData?.deals || {};
  const unreadTotal = unreadData?.total || 0;
  const markRead = useMutation({
    mutationFn: async (id) => api.post(`/deals/${id}/mark-read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deals-unread"] }),
  });
  const markAllRead = useMutation({
    mutationFn: async () => api.post("/deals/unread/mark-all"),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["deals-unread"] }); toast.success("All deal updates marked as read"); },
  });

  // Opening a deal auto-clears its unread badge.
  useEffect(() => {
    if (open && editing?.id && unreadMap[editing.id]) markRead.mutate(editing.id);
  }, [open, editing?.id, unreadMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const coMap = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c.name])), [companies]);

  const { search: locSearch } = useLocation();
  // URL ⇄ dialog sync: the open deal lives in ?open={id}, so browser Back from a
  // quote (or anywhere) restores the exact deal that was open.
  useEffect(() => {
    const sp = new URLSearchParams(locSearch);
    const oid = sp.get("open");
    if (oid) {
      if (open && editing?.id === oid) return;
      const tab = ["details", "activities", "notes", "attachments"].includes(sp.get("tab")) ? sp.get("tab") : "details";
      api.get(`/deals/${oid}`).then(({ data: d }) => {
        setEditing({ ...d, company_id: d.company_id || "none", contact_id: d.contact_id || "none" });
        setDealTab(tab);
        setOpen(true);
      }).catch(() => { toast.error("That deal could not be found"); nav("/deals", { replace: true }); });
    } else if (open && editing?.id) {
      setOpen(false);
    }
  }, [locSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // state → URL: opening a saved deal pushes ?open={id}; manual close clears it.
  useEffect(() => {
    if (!window.location.pathname.startsWith("/deals")) return;
    const sp = new URLSearchParams(window.location.search);
    const oid = sp.get("open");
    if (open && editing?.id && oid !== editing.id) {
      nav(`/deals?open=${editing.id}`);
    } else if (!open && oid) {
      nav("/deals", { replace: true });
    }
  }, [open, editing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [wonGuard, setWonGuard] = useState(null); // {dealId, quotes} — Won blocked until a quote is accepted
  const handleWonGuard = (e, dealId) => {
    const d = e?.response?.data?.detail_raw || e?.response?.data?.detail;
    if (e?.response?.status === 409 && d?.code === "quote_acceptance_required") {
      const prevStage = deals.find((x) => x.id === dealId)?.stage;
      setWonGuard({ dealId, quotes: d.quotes || [], prevStage });
      return true;
    }
    return false;
  };

  const save = useMutation({
    mutationFn: async (body) => {
      const payload = {
        ...body,
        company_id: body.company_id === "none" ? null : body.company_id,
        contact_id: body.contact_id === "none" ? null : body.contact_id,
        value: parseFloat(body.value) || 0,
      };
      if (body.id) return (await api.patch(`/deals/${body.id}`, payload)).data;
      return (await api.post("/deals", payload)).data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      setOpen(false);
      if (data?.auto_job_id) {
        toast.success("Deal won — job created, awaiting 50% deposit", {
          action: { label: "Open job", onClick: () => nav(`/jobs/${data.auto_job_id}`) },
        });
      } else toast.success("Saved");
    },
    onError: (e, body) => {
      if (!handleWonGuard(e, body?.id)) toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Save failed");
    },
  });
  const move = useMutation({
    mutationFn: async ({ id, stage }) => (await api.patch(`/deals/${id}`, { stage })).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      qc.invalidateQueries({ queryKey: ["activities"] });
      qc.invalidateQueries({ queryKey: ["daily-tasks"] });
      if (data?.auto_job_id) {
        toast.success("Deal won — job created, awaiting 50% deposit", {
          action: { label: "Open job", onClick: () => nav(`/jobs/${data.auto_job_id}`) },
        });
      }
    },
    onError: (e, vars) => {
      if (!handleWonGuard(e, vars?.id)) toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not move the deal");
    },
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/deals/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["deals"] }); toast.success("Deleted"); },
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onDragEnd = (e) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over || !active.data?.current) return;
    const deal = active.data.current.deal;
    const newStage = over.id;
    if (deal.stage !== newStage) {
      move.mutate({ id: deal.id, stage: newStage });
    }
  };

  const dealsU = useMemo(() => deals.map((d) => (unreadMap[d.id] ? { ...d, _unread: unreadMap[d.id] } : d)), [deals, unreadMap]);
  const grouped = STAGE_ORDER.reduce((acc, s) => ({ ...acc, [s]: dealsU.filter((d) => d.stage === s) }), {});
  const activeDeal = activeId ? dealsU.find((d) => d.id === activeId) : null;

  return (
    <div>
      <SelectAcceptedQuoteDialog
        guard={wonGuard}
        onClose={() => {
          if (open && editing?.id && editing.id === wonGuard?.dealId && wonGuard?.prevStage) {
            setEditing((p) => ({ ...p, stage: wonGuard.prevStage }));
          }
          setWonGuard(null);
        }}
        onAccepted={(jobId, quote) => {
          const g = wonGuard;
          setWonGuard(null);
          qc.invalidateQueries({ queryKey: ["deals"] });
          qc.invalidateQueries({ queryKey: ["quotes"] });
          qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
          qc.invalidateQueries({ queryKey: ["jobs"] });
          if (open && editing?.id && editing.id === g?.dealId) {
            save.mutate({ ...editing, stage: "won" }); // persists any other form edits — guard passes now
          }
          if (jobId) {
            toast.success(`Quote ${quote.quote_number} accepted — deal won, job created, awaiting 50% deposit`, {
              action: { label: "Open job", onClick: () => nav(`/jobs/${jobId}`) },
            });
          } else toast.success(`Quote ${quote.quote_number} accepted — deal moved to Won`);
        }}
      />
      <PageHeader
        title="Deals Pipeline"
        subtitle="Drag deals between stages. Automation rules trigger on stage change."
        breadcrumb={["Sales", "Deals"]}
        actions={
          <div className="flex items-center gap-2">
            <div className="flex border border-slate-300 rounded-sm overflow-hidden">
              <button data-testid="view-kanban-btn" onClick={() => setView("kanban")} className={`px-3 h-10 flex items-center gap-1.5 text-xs font-semibold ${view === "kanban" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                <LayoutGrid size={14} /> Kanban
              </button>
              <button data-testid="view-list-btn" onClick={() => setView("list")} className={`px-3 h-10 flex items-center gap-1.5 text-xs font-semibold border-l border-slate-300 ${view === "list" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                <List size={14} /> List
              </button>
            </div>
            <Button data-testid="new-deal-btn" onClick={() => { setEditing({ ...EMPTY, expected_close: plus48h() }); setDealTab("details"); setOpen(true); }} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
              <Plus size={16} className="mr-2" /> New deal
            </Button>
          </div>
        }
      />

      <div className="px-4 md:px-6 lg:px-10 py-4 md:py-6">
        <div className="flex flex-col sm:flex-row gap-2 md:gap-3 mb-4">
          <div className="relative flex-1 sm:max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="deal-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search deals..." className="pl-9 h-10 rounded-sm" />
          </div>
          <div className="flex gap-2">
            <Select value={openClosed} onValueChange={setOpenClosed}>
              <SelectTrigger className="w-1/2 sm:w-36 h-10 rounded-sm" data-testid="deal-open-closed-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open deals</SelectItem>
                <SelectItem value="closed">Closed (won/lost)</SelectItem>
                <SelectItem value="all">All deals</SelectItem>
              </SelectContent>
            </Select>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="w-1/2 sm:w-44 h-10 rounded-sm" data-testid="deal-stage-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stages</SelectItem>
                {STAGE_ORDER.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
            <DefaultViewControl screen="deals" className="h-10"
              current={{ view, open_closed: openClosed, stage: stageFilter }}
              onApply={(f) => { if (f.view) setView(f.view); if (f.open_closed) setOpenClosed(f.open_closed); if (f.stage) setStageFilter(f.stage); }} />
          </div>
          {unreadTotal > 0 && (
            <div className="flex items-center gap-2 sm:ml-auto">
              <span data-testid="deals-unread-total" className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 whitespace-nowrap">
                {unreadTotal} unread update{unreadTotal === 1 ? "" : "s"}
              </span>
              <Button size="sm" variant="outline" data-testid="deals-mark-all-read" onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending} className="h-9 rounded-sm text-xs whitespace-nowrap">
                <CheckCheck size={14} className="mr-1.5" /> Mark all read
              </Button>
            </div>
          )}
        </div>

        {view === "kanban" ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={(e) => setActiveId(e.active.id)} onDragEnd={onDragEnd}>
            <div className="flex gap-3 overflow-x-auto pb-4">
              {STAGE_ORDER.map((s) => (
                <KanbanColumn key={s} stage={s} deals={grouped[s]} coMap={coMap} onEdit={(d) => { setEditing({ ...d, company_id: d.company_id || "none", contact_id: d.contact_id || "none" }); setDealTab("details"); setOpen(true); }} />
              ))}
            </div>
            <DragOverlay>{activeDeal && <DealCard deal={activeDeal} coMap={coMap} dragging />}</DragOverlay>
          </DndContext>
        ) : (
          <Card className="rounded-sm border-slate-200 industrial-shadow overflow-hidden">
            {/* Mobile: stacked cards. Table view on ≥md keeps the dense read-out. */}
            <div className="md:hidden divide-y divide-slate-100" data-testid="deals-mobile-list">
              {dealsU.map((d) => (
                <div key={d.id} className={`p-3 hover:bg-cyan-50/30 active:bg-cyan-100/40 ${d._unread ? "bg-sky-50/70" : ""}`}
                  data-testid={`deal-row-${d.id}`}
                  onClick={() => { setEditing({ ...d, company_id: d.company_id || "none", contact_id: d.contact_id || "none" }); setDealTab("details"); setOpen(true); }}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {d._unread && <span className="w-2 h-2 rounded-full bg-sky-500 shrink-0" data-testid={`deal-unread-dot-${d.id}`} />}
                        <span className="font-mono text-[11px] font-semibold text-cyan-700">{d.job_number || "—"}</span>
                        <Badge className={`rounded-sm text-[9px] ${STAGE_COLORS[d.stage]}`} variant="outline">{STAGE_LABELS[d.stage]}</Badge>
                        {d._unread && <Badge data-testid={`deal-unread-pill-${d.id}`} className="rounded-full text-[9px] bg-amber-400 text-slate-900 border-0 font-bold hover:bg-amber-400">{d._unread} new</Badge>}
                      </div>
                      <div className="text-sm font-medium text-slate-900 mt-1 truncate">{d.title}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5 truncate">{coMap[d.company_id] || "No company"}</div>
                    </div>
                    <div className="text-right shrink-0 min-w-[80px]">
                      <div className="font-mono text-sm font-semibold text-slate-900">{fmtCurrency(d.value)}</div>
                      {d.fence_type && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{FENCE_LABELS[d.fence_type] || "—"}</div>}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                    <span className="text-[10px] text-slate-400">{d.expected_close ? `Quote: ${fmtDate(d.expected_close)}` : "No quote date"}</span>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      {d._unread && <Button size="sm" variant="ghost" className="h-8 px-2 text-emerald-700 text-[11px]" data-testid={`deal-mark-read-${d.id}`} onClick={() => markRead.mutate(d.id)}><Check size={13} className="mr-1" /> Read</Button>}
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`deal-edit-${d.id}`} onClick={() => { setEditing({ ...d, company_id: d.company_id || "none", contact_id: d.contact_id || "none" }); setDealTab("details"); setOpen(true); }}><Pencil size={13} /></Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600" data-testid={`deal-del-${d.id}`} onClick={() => del.mutate(d.id)}><Trash2 size={13} /></Button>
                    </div>
                  </div>
                </div>
              ))}
              {deals.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No deals</div>}
            </div>
            <table className="w-full text-sm hidden md:table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left stamp-label text-slate-500">
                  <th className="px-4 py-3">Job #</th>
                  <th className="px-4 py-3">Deal</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Stage</th>
                  <th className="px-4 py-3">Fence type</th>
                  <th className="px-4 py-3 text-right">Value</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Expected quote</th>
                  <th className="px-4 py-3 w-20"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dealsU.map((d) => (
                  <tr key={d.id} className={`hover:bg-cyan-50/30 cursor-pointer ${d._unread ? "bg-sky-50/60" : ""}`} data-testid={`deal-row-desktop-${d.id}`}
                    onClick={() => { setEditing({ ...d, company_id: d.company_id || "none", contact_id: d.contact_id || "none" }); setDealTab("details"); setOpen(true); }}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-cyan-700">
                      <span className="inline-flex items-center gap-1.5">
                        {d._unread && <span className="w-2 h-2 rounded-full bg-sky-500" data-testid={`deal-unread-dot-desktop-${d.id}`} />}
                        {d.job_number || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {d.title}
                      {d._unread && <Badge data-testid={`deal-unread-pill-desktop-${d.id}`} className="ml-2 rounded-full text-[9px] bg-amber-400 text-slate-900 border-0 font-bold hover:bg-amber-400">{d._unread} new</Badge>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{coMap[d.company_id] || "—"}</td>
                    <td className="px-4 py-3"><Badge className={`rounded-sm text-[10px] ${STAGE_COLORS[d.stage]}`} variant="outline">{STAGE_LABELS[d.stage]}</Badge></td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{FENCE_LABELS[d.fence_type] || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{fmtCurrency(d.value)}</td>
                    <td className="px-4 py-3 text-slate-500 hidden lg:table-cell text-xs">{fmtDate(d.expected_close)}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 items-center">
                        {d._unread && <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-700 text-[11px]" data-testid={`deal-mark-read-desktop-${d.id}`} onClick={() => markRead.mutate(d.id)} title="Mark as read"><Check size={12} className="mr-1" />Read</Button>}
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing({ ...d, company_id: d.company_id || "none", contact_id: d.contact_id || "none" }); setDealTab("details"); setOpen(true); }}><Pencil size={12} /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => del.mutate(d.id)}><Trash2 size={12} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {deals.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">No deals</td></tr>}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-xl" data-testid="deal-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">
            {editing?.id ? <>Edit deal {editing.job_number && <span className="font-mono text-sm text-cyan-700 ml-2">{editing.job_number}</span>}</> : "New deal"}
          </DialogTitle></DialogHeader>
          {editing?.id && (
            <div className="flex border-b border-slate-200 -mt-1">
              {/* Iter-120: Details / Activities & Timeline / Notes (chat) / Attachments. */}
              {[["details", "Details"], ["activities", "Activities & Timeline"], ["notes", "Notes"], ["attachments", "Attachments"]].map(([k, label]) => (
                <button key={k} type="button" data-testid={`deal-tab-${k}`} onClick={() => setDealTab(k)}
                  className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px ${dealTab === k ? "border-cyan-600 text-cyan-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
                  {label}
                </button>
              ))}
            </div>
          )}
          {editing?.id && dealTab === "activities" ? (
            <DealActivities dealId={editing.id} editingDeal={editing} />
          ) : editing?.id && dealTab === "notes" ? (
            <DealChat dealId={editing.id} />
          ) : editing?.id && dealTab === "attachments" ? (
            <DealAttachments dealId={editing.id} />
          ) : editing && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              {editing.id && <DealQuoteLink dealId={editing.id} nav={nav} onClose={() => setOpen(false)} />}
              {editing.id && editing.company_id && editing.company_id !== "none" && (
                <DealClientCard companyId={editing.company_id} contactId={editing.contact_id !== "none" ? editing.contact_id : null} nav={nav} onClose={() => setOpen(false)} />
              )}
              <Field label="Title" required><Input data-testid="deal-title-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Company">
                  <Select value={editing.company_id} onValueChange={(v) => setEditing({ ...editing, company_id: v })}>
                    <SelectTrigger data-testid="deal-company-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Contact">
                  <Select value={editing.contact_id} onValueChange={(v) => setEditing({ ...editing, contact_id: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Stage">
                  <Select value={editing.stage} onValueChange={(v) => setEditing({ ...editing, stage: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGE_ORDER.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Fence type">
                  <Select value={editing.fence_type || "chain_wire"} onValueChange={(v) => setEditing({ ...editing, fence_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{fenceTypes.filter(f => !["labour","incidental"].includes(f.value)).map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Value (AUD)"><Input type="number" data-testid="deal-value-input" value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} /></Field>
                <Field label="Expected quote date"><Input type="date" data-testid="deal-expected-quote-date" value={editing.expected_close ? editing.expected_close.substring(0,10) : ""} onChange={(e) => setEditing({ ...editing, expected_close: e.target.value })} /></Field>
              </div>
              <Field label="Tags">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(editing.tags || []).map((t, i) => (
                    <Badge key={`tag-${t}`} variant="outline" className="rounded-sm cursor-pointer" onClick={() => setEditing({ ...editing, tags: editing.tags.filter((_, idx) => idx !== i) })}>{t} ×</Badge>
                  ))}
                </div>
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => {
                  if (e.key === "Enter" && tagInput.trim()) {
                    e.preventDefault();
                    setEditing({ ...editing, tags: [...(editing.tags || []), tagInput.trim()] });
                    setTagInput("");
                  }
                }} placeholder="Press Enter to add" />
              </Field>
              {/* Iter-67: Notes textarea removed from Details tab. All
                  notes now live in the merged "Notes & Timeline" tab so
                  there's one canonical place per deal. Legacy `notes`
                  field is still returned by the API and shown as the
                  first entry on that tab. */}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            {(dealTab === "details" || !editing?.id) && <Button data-testid="save-deal-btn" onClick={() => save.mutate(editing)} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">Save deal</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KanbanColumn({ stage, deals, coMap, onEdit }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  const total = deals.reduce((sum, d) => sum + (d.value || 0), 0);
  const step = STAGE_STEP[stage];
  const terminal = stage === "won" || stage === "lost";
  const stepColor = stage === "won" ? "bg-emerald-600" : stage === "lost" ? "bg-rose-500" : "bg-cyan-600";
  return (
    <div ref={setNodeRef} data-testid={`kanban-col-${stage}`} className={`flex-shrink-0 w-72 ${isOver ? "bg-cyan-50" : "bg-slate-50"} border border-slate-200 transition-colors`}>
      <div className="px-3 py-2.5 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`shrink-0 w-6 h-6 rounded-sm ${stepColor} text-white font-display font-bold text-xs flex items-center justify-center`}>
              {terminal ? (stage === "won" ? "✓" : "×") : step}
            </div>
            <div className="stamp-label text-slate-700 truncate">{STAGE_LABELS[stage]}</div>
          </div>
          <div className="shrink-0 flex items-center gap-1 text-[10px]">
            <span className="text-slate-400 uppercase tracking-wider">Deals</span>
            <span className="font-mono font-semibold text-slate-900">{deals.length}</span>
          </div>
        </div>
        <div className="font-mono text-xs text-slate-500 mt-1 flex justify-between">
          <span className="text-slate-400 uppercase tracking-wider stamp-label">Value</span>
          <span className="font-semibold text-slate-900">{fmtCurrency(total)}</span>
        </div>
      </div>
      <div className="p-2 space-y-2 min-h-[200px]">
        {deals.map((d) => <DraggableDealCard key={d.id} deal={d} coMap={coMap} onEdit={onEdit} />)}
        {deals.length === 0 && <div className="text-center text-xs text-slate-400 py-8">Drop here</div>}
      </div>
    </div>
  );
}

function DraggableDealCard({ deal, coMap, onEdit }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: deal.id, data: { deal } });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`deal-card-${deal.id}`}
      className={`${isDragging ? "opacity-30" : ""}`}
      onClick={() => onEdit(deal)}
    >
      <DealCard deal={deal} coMap={coMap} onEdit={onEdit} />
    </div>
  );
}

function DealCard({ deal, coMap, dragging, onEdit }) {
  return (
    <div className={`bg-white border ${dragging ? "border-cyan-400 shadow-lg" : "border-slate-200 hover:border-slate-400"} p-3 cursor-grab active:cursor-grabbing transition-colors`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="font-medium text-sm text-slate-900 leading-tight">
          {deal._unread && <span className="inline-block w-2 h-2 rounded-full bg-sky-500 mr-1.5" data-testid={`deal-card-unread-dot-${deal.id}`} />}
          {deal.title}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {deal._unread && <span className="bg-amber-400 text-slate-900 text-[9px] font-bold rounded-full px-1.5 py-0.5 leading-none" data-testid={`deal-card-unread-${deal.id}`}>{deal._unread} new</span>}
          {deal.job_number && <span className="font-mono text-[9px] font-semibold text-cyan-700">{deal.job_number}</span>}
        </div>
      </div>
      {deal.company_id && <div className="text-[11px] text-slate-500 mb-2">{coMap[deal.company_id]}</div>}
      <div className="flex items-center justify-between">
        <div className="font-mono text-sm font-semibold text-slate-900">{fmtCurrency(deal.value)}</div>
        {deal.fence_type && <Badge variant="outline" className="rounded-sm text-[9px] border-slate-200 text-slate-500 font-normal">{FENCE_LABELS[deal.fence_type]}</Badge>}
      </div>
      {(deal.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {deal.tags.map((t) => <span key={`dt-${t}`} className="inline-flex items-center text-[9px] px-1.5 py-0.5 bg-cyan-50 text-cyan-700 border border-cyan-200"><Tag size={8} className="mr-0.5" />{t}</span>)}
        </div>
      )}
      {deal.expected_close && <div className="text-[10px] text-slate-400 mt-2 flex items-center gap-1"><CalIcon size={10} />{fmtDate(deal.expected_close)}</div>}
    </div>
  );
}

function DealClientCard({ companyId, contactId, nav, onClose }) {
  // Iter-67: mirrors the client card shown in ActivityDetailDialog so
  // the estimator has a one-tap link into the client profile (with
  // primary contact + phone / mobile / email) from the Deals pipeline.
  const { data: company } = useQuery({
    queryKey: ["deal-client-company", companyId],
    queryFn: async () => (await api.get(`/companies`)).data.find((c) => c.id === companyId),
    enabled: !!companyId,
  });
  const { data: contacts = [] } = useQuery({
    queryKey: ["deal-client-contacts", companyId],
    queryFn: async () => (await api.get(`/contacts`, { params: { company_id: companyId } })).data,
    enabled: !!companyId,
  });
  if (!company) return null;
  const contact = (contactId && contacts.find((c) => c.id === contactId))
    || contacts.find((c) => c.is_primary)
    || contacts[0];
  const phone = contact?.mobile_phone || contact?.phone || company?.phone || "";
  const email = contact?.email || company?.email || "";
  const address = company?.billing_address || company?.address || "";
  const contactName = contact ? `${contact.first_name || ""} ${contact.last_name || ""}`.trim() : "";
  return (
    <div className="bg-cyan-50/60 border border-cyan-200 rounded-sm px-3 py-2.5 space-y-1" data-testid="deal-client-card">
      <button
        type="button"
        onClick={() => { onClose(); nav(`/clients/${companyId}`); }}
        className="flex items-center gap-1.5 text-sm font-semibold text-cyan-700 hover:underline"
        data-testid="deal-client-name-link"
      >
        <User size={13} /> {company.name}
      </button>
      {contactName && (
        <div className="flex items-center gap-1.5 text-xs text-slate-600" data-testid="deal-client-contact">
          <User size={11} className="text-slate-400" />
          {contactName}
        </div>
      )}
      {phone && (
        <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-cyan-700" data-testid="deal-client-phone">
          <Phone size={11} className="text-emerald-600" />
          <span className="font-mono">{phone}</span>
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`} className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-cyan-700" data-testid="deal-client-email">
          <Mail size={11} className="text-cyan-600" />
          <span className="truncate">{email}</span>
        </a>
      )}
      {address && (
        <a
          href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
          target="_blank" rel="noreferrer"
          className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-cyan-700"
          data-testid="deal-client-address"
        >
          <MapPin size={11} className="text-rose-600" />
          <span className="truncate">{address}</span>
        </a>
      )}
    </div>
  );
}

function DealQuoteLink({ dealId, nav, onClose }) {
  const { data: deal } = useQuery({
    queryKey: ["deal-full", dealId],
    queryFn: async () => (await api.get(`/deals/${dealId}`)).data,
  });
  const quotes = deal?.quotes || [];
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2.5" data-testid="deal-quote-link">
      <div className="stamp-label text-slate-500 mb-1.5">Linked quote</div>
      {quotes.length > 0 ? (
        <div className="space-y-1">
          {quotes.map((q) => (
            <button key={q.id} type="button" onClick={() => { onClose(); nav(`/quotes/v2/${q.id}`); }}
              className="flex items-center gap-1.5 text-sm text-cyan-700 hover:underline font-semibold" data-testid={`deal-quote-open-${q.id}`}>
              <FileText size={13} /> {q.quote_number} <span className="text-slate-400 font-normal text-xs capitalize">· {q.status}</span>
            </button>
          ))}
        </div>
      ) : (
        <Button size="sm" onClick={() => { onClose(); nav(`/quotes/new?deal=${dealId}`); }}
          className="rounded-sm h-8 bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="deal-create-quote-btn">
          <Plus size={13} className="mr-1.5" /> Create quote for this deal
        </Button>
      )}
      {deal?.job && (
        <button type="button" onClick={() => { onClose(); nav(`/jobs/${deal.job.id}`); }}
          className="flex items-center gap-1.5 text-xs text-emerald-700 hover:underline mt-1.5" data-testid="deal-job-link">
          <CheckCircle2 size={12} /> Install job: {deal.job.job_number || deal.job.title} <span className="text-slate-400 capitalize">· {deal.job.status}</span>
        </button>
      )}
    </div>
  );
}

function DealActivities({ dealId, editingDeal }) {
  const qc = useQueryClient();
  const [sel, setSel] = useState(null);
  const [creating, setCreating] = useState(null);
  const { data: acts = [], isLoading } = useQuery({
    queryKey: ["deal-activities", dealId],
    queryFn: async () => (await api.get("/activities", { params: { deal_id: dealId } })).data,
  });
  const toggle = useMutation({
    mutationFn: async ({ id, completed }) => (await api.patch(`/activities/${id}`, { completed })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-activities"] });
      qc.invalidateQueries({ queryKey: ["activities"] });
      qc.invalidateQueries({ queryKey: ["deal-timeline", dealId] });
    },
  });
  const openActs = acts.filter((a) => !a.completed);
  // Iter-67: pre-link a new activity to THIS deal so the estimator doesn't
  // have to re-pick it in the dialog.
  const startNew = () => setCreating({
    type: "task",
    title: "", description: "", due_at: "",
    deal_id: dealId,
    company_id: editingDeal?.company_id || null,
    contact_id: editingDeal?.contact_id || null,
    completed: false, priority: "medium", assigned_to: null,
  });
  const Row = ({ a }) => (
    <div className="flex items-center gap-2 py-2 border-b border-slate-100 last:border-0" data-testid={`deal-activity-${a.id}`}>
      <button type="button" onClick={() => toggle.mutate({ id: a.id, completed: !a.completed })}
        className={`shrink-0 ${a.completed ? "text-emerald-600" : "text-slate-300 hover:text-emerald-500"}`}
        data-testid={`deal-activity-toggle-${a.id}`} title={a.completed ? "Re-open" : "Mark complete"}>
        <CheckCircle2 size={16} />
      </button>
      <button type="button" onClick={() => setSel(a)} className="flex-1 min-w-0 text-left" data-testid={`deal-activity-open-${a.id}`}>
        <div className={`text-sm ${a.completed ? "line-through text-slate-400" : "text-slate-800"}`}>{a.title}</div>
        <div className="text-[11px] text-slate-400">
          <span className="capitalize">{a.type}</span>
          {a.due_at && <> · due {fmtDateTime(a.due_at)}</>}
          {a.assigned_to_name && <> · {a.assigned_to_name}</>}
        </div>
      </button>
    </div>
  );
  return (
    <div className="max-h-[60vh] overflow-y-auto pr-1" data-testid="deal-activities-panel">
      {isLoading && <div className="py-8 text-center text-slate-400 text-sm"><Loader2 size={16} className="animate-spin inline" /></div>}
      <div className="flex items-center justify-between mb-2">
        <div className="stamp-label text-slate-500">Open activities ({openActs.length})</div>
        <Button
          size="sm"
          onClick={startNew}
          className="rounded-sm h-8 text-xs bg-cyan-600 hover:bg-cyan-700 text-white"
          data-testid="deal-add-activity-btn"
        >
          <Plus size={13} className="mr-1" /> Add activity
        </Button>
      </div>
      {openActs.map((a) => <Row key={a.id} a={a} />)}
      {openActs.length === 0 && !isLoading && <div className="text-xs text-slate-400 py-2">Nothing open for this deal.</div>}
      <DealTimelineFeed dealId={dealId} />
      <ActivityDetailDialog activity={sel} open={!!sel} onClose={() => setSel(null)} />
      <ActivityDetailDialog
        activity={creating}
        open={!!creating}
        onClose={() => setCreating(null)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["deal-activities", dealId] });
          qc.invalidateQueries({ queryKey: ["activities"] });
          qc.invalidateQueries({ queryKey: ["deal-timeline", dealId] });
          setCreating(null);
        }}
      />
    </div>
  );
}

// ---- Timeline feed (Iter-120): milestones + completed activities, newest first ----
const TL_STYLE = {
  "deal": ["bg-slate-900 text-white", Star],
  "quote-created": ["bg-cyan-100 text-cyan-700", FileText],
  "quote-submitted": ["bg-amber-100 text-amber-700", Hourglass],
  "quote-changes": ["bg-rose-100 text-rose-700", Undo2],
  "quote-approved": ["bg-emerald-100 text-emerald-700", ShieldCheck],
  "quote-revised": ["bg-slate-100 text-slate-600", Pencil],
  "quote-sent": ["bg-cyan-100 text-cyan-700", Mail],
  "quote-accepted": ["bg-emerald-600 text-white", Check],
  "quote-declined": ["bg-rose-600 text-white", XCircle],
  "activity": ["bg-slate-100 text-slate-500", Check],
  "booking": ["bg-cyan-100 text-cyan-700", CalIcon],
  "job": ["bg-slate-900 text-white", Hammer],
};

function DealTimelineFeed({ dealId }) {
  const { data } = useQuery({
    queryKey: ["deal-timeline", dealId],
    queryFn: async () => (await api.get(`/timeline/deal/${dealId}`)).data,
  });
  const events = data?.events || [];
  return (
    <div className="mt-4 pt-3 border-t border-slate-200" data-testid="deal-timeline-feed">
      <div className="flex items-center justify-between mb-1">
        <div className="stamp-label text-slate-500">Timeline ({events.length})</div>
        <div className="text-[10px] text-slate-400">newest first</div>
      </div>
      {events.map((ev, i) => {
        const key = ev.kind === "quote" ? `quote-${ev.status}` : ev.kind;
        const [cls, Icon] = TL_STYLE[key] || TL_STYLE.activity;
        const done = ev.kind === "activity";
        return (
          <div key={ev.id || i} className="flex gap-2.5 py-1.5 relative" data-testid={`tl-event-${i}`}>
            {i < events.length - 1 && <div className="absolute left-[11px] top-8 bottom-[-6px] w-px bg-slate-200" />}
            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 z-10 ${cls}`}><Icon size={12} /></div>
            <div className="min-w-0">
              <div className="text-[12.5px] text-slate-800 leading-snug">
                <span className={done ? "line-through text-slate-400" : "font-medium"}>{ev.title}</span>
                {ev.detail && <span className="text-slate-500"> — {ev.detail}</span>}
              </div>
              <div className="text-[10px] text-slate-400 font-mono">{fmtDateTime(ev.date)}</div>
            </div>
          </div>
        );
      })}
      {events.length === 0 && <div className="text-xs text-slate-400 py-2">No history yet.</div>}
    </div>
  );
}

// ---- Notes chat (Iter-120): Teams/WhatsApp-style with @mentions + attachments ----
const highlightMentions = (txt, mens) => {
  const names = (mens || []).map((m) => m.name).filter(Boolean);
  if (!names.length || !txt) return txt;
  const re = new RegExp(`@(${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "g");
  const out = [];
  let last = 0, match, i = 0;
  while ((match = re.exec(txt))) {
    if (match.index > last) out.push(txt.slice(last, match.index));
    out.push(<span key={i++} className="font-bold underline decoration-dotted underline-offset-2">{match[0]}</span>);
    last = match.index + match[0].length;
  }
  out.push(txt.slice(last));
  return out;
};

function DealChat({ dealId }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [staged, setStaged] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [mentionQ, setMentionQ] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const { data } = useQuery({
    queryKey: ["deal-chat", dealId],
    queryFn: async () => (await api.get(`/deals/${dealId}/chat`)).data,
    refetchInterval: 15000,
  });
  const { data: staff = [] } = useQuery({
    queryKey: ["mentionable-users"],
    queryFn: async () => (await api.get("/auth/users/mentionable")).data,
    staleTime: 300000,
  });
  const msgs = data?.messages || [];
  useEffect(() => { boxRef.current?.scrollTo({ top: boxRef.current.scrollHeight }); }, [msgs.length]);

  const onType = (v) => {
    setText(v);
    const m = /@([A-Za-z0-9'’-]*)$/.exec(v);
    setMentionQ(m ? m[1].toLowerCase() : null);
  };
  const pickMention = (u) => {
    setText((t) => t.replace(/@([A-Za-z0-9'’-]*)$/, `@${u.name} `));
    setMentions((cur) => (cur.some((x) => x.id === u.id) ? cur : [...cur, { id: u.id, name: u.name }]));
    setMentionQ(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  };
  const send = async () => {
    if (sending || (!text.trim() && staged.length === 0)) return;
    setSending(true);
    try {
      const ids = [];
      for (const f of staged) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("deal_id", dealId);
        const r = await api.post("/timeline/files", fd);
        ids.push(r.data.id);
      }
      const active = mentions.filter((m) => text.includes(`@${m.name}`));
      await api.post(`/deals/${dealId}/notes`, { text: text.trim(), attachment_ids: ids, mentions: active });
      setText(""); setStaged([]); setMentions([]); setMentionQ(null);
      qc.invalidateQueries({ queryKey: ["deal-chat", dealId] });
      qc.invalidateQueries({ queryKey: ["deal-files", dealId] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the note");
    } finally { setSending(false); }
  };
  const candidates = mentionQ !== null
    ? staff.filter((u) => u.id !== user?.id && (u.name || u.email || "").toLowerCase().includes(mentionQ)).slice(0, 5)
    : [];
  const dayOf = (iso) => {
    try {
      const d = new Date(iso);
      const today = new Date();
      if (d.toDateString() === today.toDateString()) return "Today";
      return d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "short" });
    } catch { return ""; }
  };
  const timeOf = (iso) => { try { return new Date(iso).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div className="flex flex-col" data-testid="deal-chat-panel">
      <div ref={boxRef} className="h-[44vh] overflow-y-auto bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-2">
        {msgs.map((m, idx) => {
          const mine = m.by && m.by === user?.id;
          const day = dayOf(m.at);
          const prevDay = idx ? dayOf(msgs[idx - 1].at) : null;
          return (
            <div key={m.id || idx}>
              {day !== prevDay && (
                <div className="flex justify-center my-2">
                  <span className="text-[10px] font-bold text-slate-500 bg-slate-200 rounded-full px-3 py-0.5">{day}</span>
                </div>
              )}
              <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 ${mine ? "bg-cyan-700 text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`} data-testid={`chat-msg-${m.id}`}>
                  {!mine && <div className="text-[10px] font-bold text-cyan-700 mb-0.5">{m.by_name}</div>}
                  {m.text && <div className="text-[13px] whitespace-pre-wrap leading-relaxed">{highlightMentions(m.text, m.mentions)}</div>}
                  {(m.attachments || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {m.attachments.map((a) => (
                        <button key={a.id} type="button" onClick={() => openTimelineFile(a.id)} data-testid={`chat-att-${a.id}`}
                          className={`inline-flex items-center gap-1.5 text-[11px] underline rounded px-2 py-1 max-w-[220px] ${mine ? "bg-white/20 text-white" : "bg-slate-100 text-cyan-700"}`}>
                          <Paperclip size={10} className="shrink-0" /> <span className="truncate">{a.label || a.filename}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className={`text-[9px] font-mono mt-1 text-right ${mine ? "text-cyan-100/80" : "text-slate-400"}`}>{timeOf(m.at)}</div>
                </div>
              </div>
            </div>
          );
        })}
        {msgs.length === 0 && <div className="text-xs text-slate-400 text-center py-8">No notes yet — start the conversation below.</div>}
      </div>

      {staged.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-2">
          {staged.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 text-[11px] bg-cyan-50 border border-cyan-200 text-cyan-800 rounded px-2 py-1">
              <Paperclip size={10} /> {f.name}
              <button type="button" onClick={() => setStaged(staged.filter((_, j) => j !== i))} className="hover:text-rose-600" data-testid={`staged-remove-${i}`}>✕</button>
            </span>
          ))}
        </div>
      )}

      <div className="relative flex items-end gap-2 pt-2">
        {candidates.length > 0 && (
          <div className="absolute bottom-full left-10 mb-1 w-60 bg-white border border-slate-300 rounded-md shadow-xl z-20 overflow-hidden" data-testid="mention-picker">
            {candidates.map((u) => (
              <button key={u.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickMention(u)} data-testid={`mention-pick-${u.id}`}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-[12.5px] hover:bg-cyan-50 border-b border-slate-100 last:border-0">
                <span className="w-6 h-6 rounded-full bg-slate-700 text-white text-[9px] font-bold flex items-center justify-center shrink-0">
                  {(u.name || u.email || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                </span>
                <span className="truncate">{u.name || u.email}</span>
                <span className="ml-auto text-[10px] text-slate-400">{(u.role || "").replace("_", " ")}</span>
              </button>
            ))}
          </div>
        )}
        <input ref={fileRef} type="file" multiple className="hidden" data-testid="chat-file-input"
          onChange={(e) => { setStaged([...staged, ...Array.from(e.target.files || [])].slice(0, 10)); e.target.value = ""; }} />
        <button type="button" onClick={() => fileRef.current?.click()} title="Attach any file"
          className="w-9 h-9 rounded-full border border-slate-300 text-slate-500 hover:text-cyan-700 hover:border-cyan-400 flex items-center justify-center shrink-0" data-testid="chat-attach-btn">
          <Paperclip size={15} />
        </button>
        <Textarea ref={inputRef} rows={1} value={text} onChange={(e) => onType(e.target.value)} placeholder="Type a note… use @ to tag a teammate"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="rounded-2xl text-sm min-h-[38px] max-h-28 resize-none py-2" data-testid="chat-input" />
        <button type="button" onClick={send} disabled={sending || (!text.trim() && staged.length === 0)}
          className="w-10 h-10 rounded-full bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white flex items-center justify-center shrink-0" data-testid="chat-send-btn">
          {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
        </button>
      </div>
      <div className="text-[10px] text-slate-400 mt-1.5 leading-relaxed">
        Type <b>@</b> to tag a teammate — they get a bell + push notification. 📎 attaches any file. Enter sends · Shift+Enter new line.
      </div>
    </div>
  );
}

// ---- Attachments (Iter-120): view-only library of chat files ----
function DealAttachments({ dealId }) {
  const { data: files = [], isLoading } = useQuery({
    queryKey: ["deal-files", dealId],
    queryFn: async () => (await api.get("/timeline/files", { params: { deal_id: dealId } })).data,
  });
  const photos = files.filter((f) => (f.content_type || "").startsWith("image/"));
  const docs = files.filter((f) => !(f.content_type || "").startsWith("image/"));
  const extOf = (f) => ((f.filename || "").includes(".") ? f.filename.split(".").pop().toUpperCase().slice(0, 5) : "FILE");
  const when = (iso) => { try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) + " " + new Date(iso).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
  const DocCard = ({ f }) => (
    <button type="button" onClick={() => openTimelineFile(f.id)} data-testid={`att-file-${f.id}`}
      className="border border-slate-200 rounded-sm overflow-hidden text-left hover:border-cyan-400 bg-white">
      <div className={`h-16 flex flex-col items-center justify-center gap-1 text-[10px] font-bold ${f.content_type === "application/pdf" ? "bg-rose-50 text-rose-700" : "bg-blue-50 text-blue-700"}`}>
        <FileText size={20} /> {extOf(f)}
      </div>
      <div className="p-2">
        <div className="text-[11px] font-semibold text-cyan-700 underline truncate">{f.label || f.filename}</div>
        <div className="text-[9.5px] text-slate-400 mt-0.5">{f.uploaded_by} · {when(f.created_at)}</div>
      </div>
    </button>
  );
  return (
    <div className="max-h-[60vh] overflow-y-auto pr-1" data-testid="deal-attachments-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="stamp-label text-slate-500">All files on this deal ({files.length})</div>
        <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-500 bg-slate-100 border border-slate-200 rounded-sm px-2 py-1">
          <Eye size={11} /> View only — add files via the Notes chat
        </span>
      </div>
      {isLoading && <div className="py-8 text-center text-slate-400"><Loader2 size={16} className="animate-spin inline" /></div>}
      {photos.length > 0 && (
        <>
          <div className="text-[11px] font-bold text-slate-600 mt-2 mb-2">Photos ({photos.length})</div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {photos.map((f) => (
              <button key={f.id} type="button" onClick={() => openTimelineFile(f.id)} data-testid={`att-photo-${f.id}`}
                className="border border-slate-200 rounded-sm overflow-hidden text-left hover:border-cyan-400 bg-white">
                <div className="aspect-square"><AuthImg fileId={f.id} alt={f.label || f.filename} /></div>
                <div className="p-1.5">
                  <div className="text-[10.5px] font-semibold text-cyan-700 underline truncate">{f.label || f.filename}</div>
                  <div className="text-[9px] text-slate-400">{f.uploaded_by} · {when(f.created_at)}</div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
      {docs.length > 0 && (
        <>
          <div className="text-[11px] font-bold text-slate-600 mt-3 mb-2">Documents ({docs.length})</div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {docs.map((f) => <DocCard key={f.id} f={f} />)}
          </div>
        </>
      )}
      {!isLoading && files.length === 0 && (
        <div className="text-xs text-slate-400 text-center py-10">Nothing attached yet — add photos or documents through the Notes chat.</div>
      )}
    </div>
  );
}
